import { GuestAddress } from '../interfaces/user';

/**
 * Safely extracts an address component by type from Google Places API response
 * @param components - Array of address components from Google Places API
 * @param type - The type of component to find (e.g., 'locality', 'country')
 * @param useShortName - Whether to return short_name instead of long_name
 * @returns The component value or empty string if not found
 */
function getAddressComponent(
  components: any[],
  type: string,
  useShortName: boolean = false
): string {
  const component = components?.find(comp => comp.types?.includes(type));
  if (!component) return '';
  
  return useShortName 
    ? (component.short_name || '') 
    : (component.long_name || '');
}

/**
 * Constructs address line 1 from street number and route components
 * Falls back to formatted_address if specific components are not available
 * @param components - Array of address components from Google Places API
 * @param formattedAddress - The formatted_address from the response as fallback
 * @returns Constructed address line 1
 */
function getAddressLine1(components: any[], formattedAddress: string): string {
  const streetNumber = getAddressComponent(components, 'street_number');
  const route = getAddressComponent(components, 'route');
  
  // If we have both street number and route, combine them
  if (streetNumber && route) {
    return `${streetNumber} ${route}`;
  }
  
  // If we have route but no street number (common in UK)
  if (route) {
    return route;
  }
  
  // If we have street number but no route
  if (streetNumber) {
    return streetNumber;
  }
  
  // Fallback to the first part of formatted address
  // This handles cases where the address structure is different
  const addressParts = formattedAddress?.split(',') || [];
  return addressParts[0]?.trim() || '';
}

/**
 * Safely parses Google Places API geocoding response into GuestAddress format
 * @param response - Response from geocodeByPlaceId
 * @param guestId - ID of the guest
 * @returns Parsed GuestAddress object
 */
export function parseGooglePlacesResponse(
  response: any[],
  guestId: number | undefined
): GuestAddress {
  if (!response || !response[0]) {
    throw new Error('Invalid Google Places API response');
  }

  const result = response[0];
  const components = result.address_components || [];
  const formattedAddress = result.formatted_address || '';

  // Extract address components with fallbacks
  const addressLine1 = getAddressLine1(components, formattedAddress);
  const city = getAddressComponent(components, 'locality') ||
               getAddressComponent(components, 'postal_town') ||
               getAddressComponent(components, 'administrative_area_level_2') ||
               getAddressComponent(components, 'sublocality');
  
  const administrativeArea = getAddressComponent(components, 'administrative_area_level_1') ||
                            getAddressComponent(components, 'administrative_area_level_2');
  
  const country = getAddressComponent(components, 'country', true); // Use short_name for country
  const zipPostal = getAddressComponent(components, 'postal_code', true); // Use short_name for postal code

  return {
    guest_id: guestId,
    address_line_1: addressLine1,
    city: city,
    administrative_area_level_1: administrativeArea,
    country: country,
    zip_postal: zipPostal,
    place_id: result.place_id || '',
  };
}
